import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Divider, Form, message, Row, Space, Steps, Tooltip } from "antd";
import { Link, useHistory } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../..";
import { IOfferingGroupAddStepsParams } from "../../types/IOfferingGroupAddOffer";
import { OfferList } from "../../../../components/OfferList/OfferList";
import { useOfferList } from "../../../../components/OfferList/utils/useOfferList";
import OfferingGroupSelectGroup from "../../features/OfferingGroupSelectGroup/OfferingGroupSelectGroup";
import "./OfferingGroupAddOffer.less";
import { FormDataOfferingGroupSelectGroup } from "../../features/OfferingGroupSelectGroup/OfferingGroupSelectGroupTypes";
import { addOfferingForGroup, offeringGroupsActions } from "../../store/slice/offeringGroupsSlice";
import { IAddOfferingForGroup } from "../../types/IOfferingGroup";
import {
  checkIsLoading,
  infinityDateHttpFormat,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";
import loadingStatusCodes from "../../../../lib/const/loadingStatusCodes";

const OfferingGroupAddOffer = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { offeringGroupsAddOffer, addOfferingForGroupLoading } = useAppSelector(
    (state) => state.offeringGroups
  );

  const [offeringGroupAddOfferParamsForm] = Form.useForm<FormDataOfferingGroupSelectGroup>();

  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  const [step, setStep] = useState<IOfferingGroupAddStepsParams["step"]>(0);

  const [activeParams, setActiveParams] = useState(false);
  const [activeVisibleGroup, setActiveVisibleGroup] = useState(false);

  const onChangeActiveParams = (newValue: boolean) => {
    setActiveParams(newValue);
    if (!newValue) {
      offeringGroupAddOfferParamsForm.setFieldsValue({
        inversion: undefined,
        defaultCardinality: undefined,
        minCardinality: undefined,
        maxCardinality: undefined,
      });
    } else {
      setActiveVisibleGroup(true);
    }
  };

  const onChangeActiveVisibleGroup = (newValue: boolean) => {
    setActiveVisibleGroup(newValue);
  };

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOfferAdd",
    initialSearchValues: { onDate: moment(), page: 0, size: 20 },
  });

  const offerListPropsTemplate = useOfferList({
    initialOfferListViewMode: "addTemplate",
    initialSelectMode: "select-template",
    initialSearchValues: { onDate: moment(), page: 0, size: 20 },
  });

  const onReset = () => {
    offerListProps.onResetSearch();
    offerListProps.setSelectedOffer(undefined);
    offerListPropsTemplate.onResetSearch();
    offerListPropsTemplate.setSelectedOffer(undefined);
    dispatch(offeringGroupsActions.cleanOfferingAddGroup());
  };

  // При нажатии пропустить сбрасываем выбранное предложение
  useEffect(() => {
    if (offerListPropsTemplate.offerListSelectMode === "skip") {
      offerListPropsTemplate.setSelectedOffer(undefined);
    }
  }, [offerListPropsTemplate.offerListSelectMode]);

  // При нажатии пропустить сбрасываем выбранное предложение
  useEffect(() => {
    if (
      offerListPropsTemplate.selectedOffer?.id &&
      offerListProps.selectedOffer?.id &&
      offerListPropsTemplate.selectedOffer.id === offerListProps.selectedOffer.id
    ) {
      offerListPropsTemplate.setSelectedOffer(undefined);
      message.destroy();
      message.error("Предложения не могут быть одинаковыми");
    }
  }, [offerListPropsTemplate.selectedOffer]);

  const nextStep: IOfferingGroupAddStepsParams["nextStep"] = () => {
    switch (step) {
      case 0:
        setStep(1);
        break;
      case 1:
        // По умолчанию вкл если выбрано (Пропустить)
        if (offerListPropsTemplate.offerListSelectMode === "skip") {
          setActiveParams(true);
          setActiveVisibleGroup(true);
        }

        // По умолчанию fromDate = onDate выбранной на шаге 0
        offeringGroupAddOfferParamsForm.setFieldsValue({
          fromDate: offerListProps.searchListValues.onDate,
          toDate: moment(infinityDateHttpFormat).endOf("day"),
        });

        setStep(2);
        break;
      case 2:
        offeringGroupAddOfferParamsForm.submit();
        break;
    }
  };

  const prevStep: IOfferingGroupAddStepsParams["prevStep"] = () => {
    switch (step) {
      case 0:
        onReset();
        history.push(`/offering-groups`);
        break;
      case 1:
        setStep(0);
        break;
      case 2:
        setStep(1);
        break;
    }
  };

  const nextBtnTitle = useMemo((): string => {
    switch (step) {
      case 0:
        return "Далее";
      case 1:
        return "Далее";
      case 2:
        return "Сохранить";
      default:
        return "";
    }
  }, [step]);

  const nextBtnActive = useMemo((): boolean => {
    switch (step) {
      case 0:
        if (offerListProps.selectedOffer) {
          return true;
        }
        return false;
      case 1:
        if (offerListPropsTemplate.offerListSelectMode === "select-template") {
          if (offerListPropsTemplate.selectedOffer) {
            return true;
          }
        }
        if (offerListPropsTemplate.offerListSelectMode === "skip") {
          return true;
        }
        return false;
      case 2:
        if (offeringGroupsAddOffer?.length === 0) {
          return false;
        }
        if (activeVisibleGroup && targetKeys.length === 0) {
          return false;
        }
        return true;
      default:
        return false;
    }
  }, [
    step,
    offerListPropsTemplate.offerListSelectMode,
    offerListPropsTemplate.selectedOffer,
    offerListProps.selectedOffer,
    offeringGroupsAddOffer,
    activeVisibleGroup,
    targetKeys,
  ]);

  const nextBtnTooltip = useMemo((): string => {
    if (nextBtnActive) {
      return "";
    }
    switch (step) {
      case 0:
        return "Необходимо выбрать предложение";
      case 1:
        return "Необходимо выбрать шаблон";
      case 2:
        if (offeringGroupsAddOffer?.length === 0) {
          return "Не найдены группы для добавления Предложения";
        }
        if (activeVisibleGroup && targetKeys.length === 0) {
          return "Выберите группы";
        }
      default:
        return "";
    }
  }, [
    step,
    nextBtnActive,
    offerListPropsTemplate.offerListSelectMode,
    offerListProps.selectedOffer,
    offeringGroupsAddOffer,
    activeVisibleGroup,
    targetKeys,
  ]);

  const onFinish = (values) => {
    const fromDate = toHttpFormatStartOfDay(values.fromDate);
    const toDate = values?.toDate ? toHttpFormatStartOfDay(values.toDate) : undefined;

    const params: IAddOfferingForGroup = {
      fromDate,
      toDate,
      offeringGroupMember: {
        memberObjectId: Number(offerListProps.selectedOffer?.id),
        memberTypeId: 4,
      },
    };

    if (offerListPropsTemplate.offerListSelectMode === "select-template") {
      params.templateMember = {
        templateMemberObjectId: Number(offerListPropsTemplate.selectedOffer?.id),
        templateMemberTypeId: 4,
      };
    }

    if (activeVisibleGroup) {
      const selectedIds = targetKeys.map((el) => Number(el));

      params.offeringGroupIds = selectedIds.length === 0 ? undefined : selectedIds;
    }

    if (activeParams) {
      params.offeringGroupMember.inversion = values.inversion === "true" || undefined;
      params.offeringGroupMember.minCardinality = values.minCardinality;
      params.offeringGroupMember.maxCardinality = values.maxCardinality;
      params.offeringGroupMember.defaultCardinality = values.defaultCardinality;

      delete params.templateMember;
    }

    dispatch(addOfferingForGroup(params)).then((res) => {
      console.log({ res });

      if (res?.meta.requestStatus === loadingStatusCodes.fulfilled) {
        onReset();
        history.push(`/offering-groups`);
      }
    });
  };

  return (
    <div className="offering-group-add-offer">
      <Row className="offering-group-add-offer-title" align="middle">
        <Link to="/offering-groups">
          <LeftOutlined style={{ fontSize: 16 }} />
        </Link>

        <h1>Добавить предложение в группы</h1>
      </Row>

      <Divider style={{ margin: "10px 0 0 0" }} />

      <Row gutter={19} style={{ marginRight: "10px" }}>
        <Col flex="auto">
          <Steps size="small" current={step}>
            <Steps.Step title="Выбор предложения" />
            <Steps.Step title="Выбор шаблона" />
            <Steps.Step title="Выбор групп" />
          </Steps>
        </Col>
        <Col>
          <Space size={20}>
            <Button className="offering-group-add-offer__stepBtn" onClick={prevStep}>
              Назад
            </Button>
            <Tooltip title={nextBtnTooltip}>
              <Button
                className="offering-group-add-offer__stepBtn"
                type="primary"
                onClick={nextStep}
                disabled={!nextBtnActive}
                loading={checkIsLoading(addOfferingForGroupLoading)}
              >
                {nextBtnTitle}
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {step !== 1 && step !== 0 && <Divider style={{ margin: "0 0 0px 0" }} />}

      {step === 0 && <OfferList filterName="group-offer-add" {...offerListProps} />}
      {step === 1 && (
        <OfferList
          filterName="group-offer-add"
          {...offerListPropsTemplate}
          isDisabled={offerListPropsTemplate.offerListSelectMode === "skip"}
        />
      )}
      {step === 2 && (
        <Form
          form={offeringGroupAddOfferParamsForm}
          onFinish={onFinish}
          labelAlign="left"
          style={{ width: "100%" }}
        >
          <OfferingGroupSelectGroup
            offerListProps={offerListProps}
            offerListPropsTemplate={offerListPropsTemplate}
            activeParams={activeParams}
            onChangeActiveParams={onChangeActiveParams}
            activeVisibleGroup={activeVisibleGroup}
            onChangeActiveVisibleGroup={onChangeActiveVisibleGroup}
            targetKeys={targetKeys}
            setTargetKeys={setTargetKeys}
          />
        </Form>
      )}
    </div>
  );
};

export default OfferingGroupAddOffer;
